'use client';

import * as React from 'react';
import { CheckIcon, ChevronDownIcon, XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from "@/lib/utils";
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';

export interface MultiSelectOption<T> {
  label: string;
  value: T;
  icon?: React.ComponentType<{ className?: string }>;
}

interface MultiSelectProps<T> {
  options: MultiSelectOption<T>[];
  selected: T[];
  onChange: (values: T[]) => void;
  placeholder?: string;
  emptyMessage?: string;
  searchPlaceholder?: string;
  className?: string;
  disabled?: boolean;
  maxCount?: number;
  contentClassName?: string;
}

const createSelectedSet = <T,>(selected: T[]) => new Set(selected);

const isSelected = <T,>(selectedSet: Set<T>, value: T) => selectedSet.has(value);

const addValue = <T,>(selected: T[], value: T) => [...selected, value];

const removeValue = <T,>(selected: T[], value: T) => selected.filter((v) => v !== value);

const toggleValue = <T,>(selected: T[], value: T) => {
  const selectedSet = createSelectedSet(selected);
  return isSelected(selectedSet, value) ? removeValue(selected, value) : addValue(selected, value);
};

const getSelectedOptions = <T,>(options: MultiSelectOption<T>[], selected: T[]) => {
  const selectedSet = createSelectedSet(selected);
  return options.filter((option) => isSelected(selectedSet, option.value));
};

const getDisplayText = <T,>(
  selectedOptions: MultiSelectOption<T>[],
  placeholder: string,
  maxCount?: number,
  countSelectedLabel?: (count: number) => string,
) => {
  if (selectedOptions.length === 0) {
    return placeholder;
  }

  // Always show count if more than maxCount (or default 2)
  const displayLimit = maxCount || 2;
  if (selectedOptions.length > displayLimit) {
    return countSelectedLabel
      ? countSelectedLabel(selectedOptions.length)
      : `${selectedOptions.length} selected`;
  }

  return null;
};

const renderSelectedBadges = <T,>(
  selectedOptions: MultiSelectOption<T>[],
  onRemove: (value: T) => void,
  disabled?: boolean,
  maxCount?: number,
  removeItemLabel?: (label: string) => string,
) => {
  const visibleOptions = maxCount ? selectedOptions.slice(0, maxCount) : selectedOptions;

  return (
    <>
      {visibleOptions.map((option) => (
        <Badge key={option.label} variant="secondary" className="hover:bg-secondary/80 gap-1 pr-0.5">
          <span className="max-w-[150px] truncate">{option.label}</span>
          <span
            role="button"
            tabIndex={disabled ? -1 : 0}
            onClick={(e) => {
              e.stopPropagation();
              if (!disabled) onRemove(option.value);
            }}
            onKeyDown={(e) => {
              if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
                e.preventDefault();
                e.stopPropagation();
                onRemove(option.value);
              }
            }}
            aria-disabled={disabled}
            aria-label={removeItemLabel ? removeItemLabel(option.label) : `Remove ${option.label}`}
            className="ring-offset-background focus:ring-ring ml-1 cursor-pointer rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:pointer-events-none"
          >
            <XIcon className="size-3" />
          </span>
        </Badge>
      ))}
      {maxCount && selectedOptions.length > maxCount && (
        <Badge variant="secondary" className="pointer-events-none">
          +{selectedOptions.length - maxCount}
        </Badge>
      )}
    </>
  );
};

function MultiSelect<T>({
  options,
  selected,
  onChange,
  placeholder,
  emptyMessage,
  searchPlaceholder,
  className,
  disabled = false,
  maxCount,
  contentClassName,
}: MultiSelectProps<T>) {
  const { t } = useTranslation('common');
  const resolvedPlaceholder = placeholder ?? t('multiSelect.placeholder');
  const resolvedEmptyMessage = emptyMessage ?? t('multiSelect.noItemsFound');
  const resolvedSearchPlaceholder = searchPlaceholder ?? t('multiSelect.searchPlaceholder');
  const [open, setOpen] = React.useState(false);

  const handleToggle = (value: T) => {
    const newSelected = toggleValue(selected, value);
    onChange(newSelected);
  };

  const handleRemove = (value: T) => {
    const newSelected = removeValue(selected, value);
    onChange(newSelected);
  };

  const handleClear = () => {
    onChange([]);
  };

  const selectedSet = createSelectedSet(selected);
  const selectedOptions = getSelectedOptions(options, selected);
  const displayText = getDisplayText(selectedOptions, resolvedPlaceholder, maxCount, (count) => t('multiSelect.countSelected', { count }));

  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn('h-auto min-h-9 w-full justify-between py-1.5', className)}
        >
          <div className="flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden">
            {displayText ? (
              <span className="text-muted-foreground truncate">{displayText}</span>
            ) : (
              renderSelectedBadges(selectedOptions, handleRemove, disabled, maxCount, (label) => t('multiSelect.removeItem', { label }))
            )}
          </div>
          <div className="ml-2 flex shrink-0 items-center gap-2">
            {selected.length > 0 && !disabled && (
              <>
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClear();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      e.stopPropagation();
                      handleClear();
                    }
                  }}
                  aria-label={t('multiSelect.clearAll')}
                  className="ring-offset-background focus:ring-ring cursor-pointer rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-2"
                >
                  <XIcon className="size-4" />
                </span>
                <Separator orientation="vertical" className="h-4" />
              </>
            )}
            <ChevronDownIcon className="size-4 shrink-0 opacity-50" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn('w-[var(--radix-popover-trigger-width)] min-w-[var(--radix-popover-trigger-width)] p-0', contentClassName)}
        align="start"
        collisionPadding={12}
      >
        <Command className="overflow-hidden">
          <CommandInput placeholder={resolvedSearchPlaceholder} />
          <CommandList className="max-h-60 overscroll-contain">
            <CommandEmpty>{resolvedEmptyMessage}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const isOptionSelected = isSelected(selectedSet, option.value);
                const Icon = option.icon;

                return (
                  <CommandItem key={option.label} onSelect={() => handleToggle(option.value)}>
                    <div
                      className={cn(
                        'border-primary mr-2 flex size-4 items-center justify-center rounded-sm border',
                        isOptionSelected
                          ? 'bg-primary text-primary-foreground'
                          : 'opacity-50 [&_svg]:invisible',
                      )}
                    >
                      <CheckIcon className="text-primary-foreground size-4" />
                    </div>
                    {Icon && <Icon className="text-muted-foreground mr-2" />}
                    <span className="text-foreground">{option.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export { MultiSelect };
